// Define step constants for the upload modal
export const UPLOAD_MODAL_STEPS = {
  DATA_STEP: 0,
  MAPPING_STEP: 1,
  UPLOAD_STEP: 2,
}

// Step labels for stepper
export const STEP_LABELS = ['Data Review', 'Mapping', 'Upload']

// Key columns (from the server's predicate) that may be blank. They still
// count for duplicates, but a blank one is not an incomplete key. Packaging
// routes key on routeId so two routes can share product and kitchen, yet most
// routes have none. Mirrors SchedulerService `constants.PREDICATE_MAPPING`.
export const OPTIONAL_PREDICATE_COLUMNS = new Set(['routeId'])

// Shared utilities for data transformation
export const dataTransformationUtils = {
  // Helper function to transform data based on mapping - shared between components
  transformDataUsingMapping: (
    sourceData: Array<Record<string, unknown>>,
    mappingRows: Array<Record<string, unknown>>
  ): Array<Record<string, unknown>> => {
    // Return empty array if there's no source data or mapping rows
    if (!sourceData || sourceData.length === 0 || !mappingRows) return []

    // Create a mapping dictionary for transformation
    // The mapping is represented as { targetField: sourceField }
    // For example: { 'productCode': 'SKUcode' } means the 'SKUcode' column in CSV maps to 'productCode' in the DB
    const targetToSourceMap: Record<string, string> = {}
    mappingRows.forEach((row) => {
      // Safety check - make sure row is valid
      if (!row) return

      const source = row.source as string
      const target = row.target as string

      // Only include mappings where both source and target are defined
      if (source && target) {
        // Map from target field to source field
        targetToSourceMap[target] = source
      }
    })

    // Transform each row of data
    return sourceData.map((sourceRow) => {
      // Safety check - make sure sourceRow is valid
      if (!sourceRow) return {} as Record<string, unknown>

      const transformedRow: Record<string, unknown> = {}

      // Preserve the id field if it exists
      if ('id' in sourceRow) {
        transformedRow['id'] = sourceRow['id']
      }

      // For each target field, get the corresponding value from the source field
      Object.entries(targetToSourceMap).forEach(
        ([targetField, sourceField]) => {
          if (sourceField in sourceRow) {
            transformedRow[targetField] = sourceRow[sourceField]
          }
        }
      )
      //console.log(transformedRow)
      return transformedRow
    })
  },

  // Helper function to generate a composite key from a row using specified columns
  getCompositeKey: (
    row: Record<string, unknown>,
    columns: string[]
  ): string => {
    // Safety check - if row is undefined or null, return a special value
    if (!row) return '[empty-row]'

    return columns
      .map((col) => {
        const value = row[col]
        // Handle different types of values explicitly
        if (value === undefined) return 'undefined'
        if (value === null) return 'null'
        if (typeof value === 'object') {
          try {
            return JSON.stringify(value)
          } catch {
            return '[complex object]'
          }
        }
        if (typeof value === 'string') return value
        if (typeof value === 'number') return value.toString()
        if (typeof value === 'boolean') return value ? 'true' : 'false'
        return '[unknown]'
      })
      .join('|')
  },

  // Generate difference data between transformed data and original data (using predicate-only matching)
  generateDifferenceData: (
    transformedData: Array<Record<string, unknown>>,
    originalData: Array<Record<string, unknown>>,
    predicateColumns: string[] = [],
    editableColumns: string[] = []
  ): Array<Record<string, unknown>> => {
    if (!transformedData.length || !originalData.length) return []
    if (!predicateColumns.length) return [] // Must have predicate columns for matching

    const differences: Array<Record<string, unknown>> = []

    // Get common columns that exist in both datasets
    const transformedColumns = new Set(
      Object.keys(transformedData[0]).filter((col) => col !== 'id')
    )
    const originalColumns = new Set(
      Object.keys(originalData[0]).filter((col) => col !== 'id')
    )
    const commonColumns = [...transformedColumns].filter((col) =>
      originalColumns.has(col)
    )

    if (commonColumns.length === 0) return [] // No common columns to compare

    // Validate predicate columns exist in both datasets
    const actualPredicateColumns = predicateColumns.filter((col) =>
      commonColumns.includes(col)
    )
    if (actualPredicateColumns.length === 0) return [] // No valid predicates for matching

    // Map original data by PREDICATE-ONLY composite key for matching
    const originalDataMap = new Map(
      originalData.map((row) => [
        dataTransformationUtils.getCompositeKey(row, actualPredicateColumns),
        row,
      ])
    )

    // Determine actual editable columns (non-predicate columns that exist in both datasets)
    const actualEditableColumns =
      editableColumns.length > 0
        ? editableColumns.filter(
            (col) =>
              commonColumns.includes(col) &&
              !actualPredicateColumns.includes(col)
          )
        : commonColumns.filter((col) => !actualPredicateColumns.includes(col))

    // For each row in transformed data, find matching row in original by predicate key
    transformedData.forEach((transformedRow, index) => {
      // Check if row has all required predicate columns
      if (actualPredicateColumns.every((col) => col in transformedRow)) {
        // Create composite key using ONLY predicate columns
        const predicateKey = dataTransformationUtils.getCompositeKey(
          transformedRow,
          actualPredicateColumns
        )
        const originalRow = originalDataMap.get(predicateKey)

        if (originalRow) {
          // Found matching row by predicate - now compare data columns
          const diffRow: Record<string, unknown> = { id: index }

          // Add all predicate columns (these identify the row)
          actualPredicateColumns.forEach((column) => {
            diffRow[column] = transformedRow[column]
          })

          // Compare data columns for changes
          let hasDifferences = false
          actualEditableColumns.forEach((column) => {
            const newValue = transformedRow[column]
            const oldValue = originalRow[column]

            // Compare values (handle null/undefined consistently)
            if (String(newValue ?? '') !== String(oldValue ?? '')) {
              diffRow[`${column}_old`] = oldValue ?? 'N/A'
              diffRow[`${column}_new`] = newValue ?? 'N/A'
              diffRow[`${column}_changed`] = true
              hasDifferences = true
            }
          })

          // Only include rows with actual differences
          if (hasDifferences) {
            differences.push(diffRow)
          }
        }
        // Note: Rows with predicate keys not found in original data are handled by generateAddedRows
      }
    })

    return differences
  },

  // Detect duplicate predicate combinations in uploaded data
  detectDuplicatePredicates: (
    data: Array<Record<string, unknown>>,
    predicateColumns: string[] = []
  ): {
    duplicates: Array<{
      key: string
      count: number
      rows: Array<Record<string, unknown>>
    }>
    hasDuplicates: boolean
  } => {
    if (!data.length || !predicateColumns.length) {
      return { duplicates: [], hasDuplicates: false }
    }

    // Group data by predicate composite key
    const predicateGroups = new Map<string, Array<Record<string, unknown>>>()

    data.forEach((row) => {
      if (predicateColumns.every((col) => col in row)) {
        const predicateKey = dataTransformationUtils.getCompositeKey(
          row,
          predicateColumns
        )
        if (!predicateGroups.has(predicateKey)) {
          predicateGroups.set(predicateKey, [])
        }
        predicateGroups.get(predicateKey)!.push(row)
      }
    })

    // Find groups with more than one row (duplicates)
    const duplicates: Array<{
      key: string
      count: number
      rows: Array<Record<string, unknown>>
    }> = []

    predicateGroups.forEach((rows, key) => {
      if (
        rows.length > 1 &&
        key !== '[empty-row]' &&
        !key.includes('undefined')
      ) {
        duplicates.push({
          key,
          count: rows.length,
          rows: rows,
        })
      }
    })

    return {
      duplicates,
      hasDuplicates: duplicates.length > 0,
    }
  },
  generateAddedRows: (
    transformedData: Array<Record<string, unknown>>,
    originalData: Array<Record<string, unknown>>,
    predicateColumns: string[] = []
  ) => {
    if (!transformedData.length || !originalData.length) return []
    if (!predicateColumns.length) return [] // Must have predicate columns for matching

    const addedRows: Array<Record<string, unknown>> = []

    // Validate predicate columns exist in both datasets
    const transformedColumns = new Set(
      Object.keys(transformedData[0]).filter((col) => col !== 'id')
    )
    const actualPredicateColumns = predicateColumns.filter((col) =>
      transformedColumns.has(col)
    )
    if (actualPredicateColumns.length === 0) return [] // No valid predicates

    // Create maps using PREDICATE-ONLY composite keys
    const originalDataPredicateKeys = new Set(
      originalData.map((row) =>
        dataTransformationUtils.getCompositeKey(row, actualPredicateColumns)
      )
    )

    // Find rows in transformed data with predicate keys not in original data
    transformedData.forEach((row) => {
      if (actualPredicateColumns.every((col) => col in row)) {
        const predicateKey = dataTransformationUtils.getCompositeKey(
          row,
          actualPredicateColumns
        )

        // Only include valid keys (not empty or with undefined values)
        if (
          predicateKey &&
          predicateKey !== '[empty-row]' &&
          !predicateKey.includes('undefined') &&
          !originalDataPredicateKeys.has(predicateKey)
        ) {
          addedRows.push(row)
        }
      }
    })

    return addedRows
  },

  // Detect rows with empty/null/undefined predicate values
  detectEmptyPredicates: (
    data: Array<Record<string, unknown>>,
    predicateColumns: string[] = []
  ): {
    emptyPredicates: Array<{
      rowIndex: number
      row: Record<string, unknown>
      emptyColumns: string[]
    }>
    hasEmptyPredicates: boolean
  } => {
    if (!data.length || !predicateColumns.length) {
      return { emptyPredicates: [], hasEmptyPredicates: false }
    }

    const emptyPredicates: Array<{
      rowIndex: number
      row: Record<string, unknown>
      emptyColumns: string[]
    }> = []

    data.forEach((row, index) => {
      const emptyColumns: string[] = []

      predicateColumns.forEach((col) => {
        // A key column that may be blank: it separates rows, but a row
        // without one is still complete.
        if (OPTIONAL_PREDICATE_COLUMNS.has(col)) return
        const value = row[col]
        // Check for null, undefined, empty string, or string 'null'
        if (
          value === null ||
          value === undefined ||
          value === '' ||
          value === 'null'
        ) {
          emptyColumns.push(col)
        }
      })

      if (emptyColumns.length > 0) {
        emptyPredicates.push({
          rowIndex: index + 1, // 1-based indexing for user display
          row: row,
          emptyColumns: emptyColumns,
        })
      }
    })

    return {
      emptyPredicates,
      hasEmptyPredicates: emptyPredicates.length > 0,
    }
  },
}
